import React, { useState, useCallback } from 'react'
import styles from './styles.module.css'
import { cn } from '@s4l/utility-css'
import useInputValue from '../hooks/useInputValue'
import Typography from '../../Typography'
import { Devices, useBrowser } from '@s4l/utils/hooks/useBrowser'

interface ICheckboxButtonProps {
  name: string
  children?: React.ReactNode
  defaultChecked?: boolean
  onChange?: (checked: boolean) => void
  disabled?: boolean
  multiple?: boolean
  trueValue?: string | boolean | number
  falseValue?: string | boolean | number
  icon?: React.ReactElement
}

const CheckboxToggleButton = ({
  name,
  children,
  defaultChecked,
  onChange,
  disabled,
  multiple,
  trueValue = true,
  falseValue = false,
  icon,
}: ICheckboxButtonProps) => {
  const [checked, setChecked] = useState(defaultChecked)
  const { device } = useBrowser()

  const convertToBoolean = useCallback(
    (value: string | boolean | number | (string | number)[]): boolean => {
      if (typeof value === 'boolean') {
        return value
      }
      if (typeof value === 'string' || typeof value === 'number') {
        return value === trueValue
      }
      if (
        multiple &&
        Array.isArray(value) &&
        (typeof trueValue === 'string' || typeof trueValue === 'number')
      ) {
        return value.includes(trueValue)
      }
      return false
    },
    [trueValue, multiple]
  )

  const { onInputChange } = useInputValue({
    name,
    defaultValue: defaultChecked,
    multiple,
    trueValue,
    falseValue,
    setInputValue: value => {
      setChecked(convertToBoolean(value))
    },
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newChecked = e.target.checked
    onInputChange(newChecked)
    onChange?.(newChecked)
  }

  const inputId = `checkbox-button-${name}-${trueValue}`

  return (
    <div className={styles.checkboxButtonWrapper}>
      <input
        id={inputId}
        name={name}
        type="checkbox"
        className={styles.hiddenCheckbox}
        checked={checked ?? false}
        onChange={handleChange}
        disabled={disabled}
      />
      <label
        htmlFor={inputId}
        className={cn(styles.checkboxButton, {
          [styles.checked]: !!checked,
          [styles.disabled]: !!disabled,
        })}
      >
        {children && (
          <Typography
            variant={device === Devices.Desktop ? 'caption' : 'body'}
            size="l"
            bold
            color={checked ? 'var(--color-primary-500)' : 'var(--color-neutral-800)'}
          >
            {children}
          </Typography>
        )}
        {icon && <span className={styles.icon}>{icon}</span>}
      </label>
    </div>
  )
}

CheckboxToggleButton.displayName = 'Form.CheckboxToggleButton'
export default CheckboxToggleButton
